/**
 * Lightweight US ZIP helpers — NO data import, safe to use in validators
 * and on every render. The ~1.8MB ZIP→centroid table is loaded separately
 * and lazily (see loadZipCentroids) only when the range check actually runs.
 */

// A 5-digit US ZIP, not part of a longer run of digits (so "7" and
// "123456789" don't match, but "Los Angeles 90001" and "90001-1234" do).
const ZIP_RE = /(?<!\d)(\d{5})(?!\d)/;

/**
 * Extract the first 5-digit US ZIP from a free-text string.
 * @param {string} text
 * @returns {string|null} the 5-digit zip, or null if none present
 */
export const extractZip = text => {
  if (!text) return null;
  const m = String(text).match(ZIP_RE);
  return m ? m[1] : null;
};

/** True when the text contains a 5-digit US ZIP. */
export const hasUsZip = text => extractZip(text) != null;

/**
 * Haversine straight-line distance between two {lat,lng} points, in miles.
 * @returns {number|null}
 */
export const haversineMiles = (a, b) => {
  if (!a || !b) return null;
  const toRad = deg => (deg * Math.PI) / 180;
  const R = 3958.8; // Earth radius, miles
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
};

// Per-first-digit shard promises, cached for the session. The table is
// sharded into 10 files (src/data/zipCentroids/<0-9>.json, ~50KB gz each)
// so a lookup mid-booking downloads one shard, not the whole ~532KB-gz
// table. Each shard is its own webpack chunk via the dynamic import below.
const zipShardCache = {};

/**
 * Look up a US ZIP centroid, lazily fetching only the shard that contains
 * it (first digit). Subsequent lookups in the same shard are instant.
 *
 * @param {string} zip 5-digit US ZIP
 * @returns {Promise<{lat,lng,city,state}|null>}
 */
export const lookupZipCentroid = async zip => {
  const z = zip && /^\d{5}$/.test(String(zip)) ? String(zip) : null;
  if (!z) return null;
  const prefix = z[0];
  if (!zipShardCache[prefix]) {
    zipShardCache[prefix] = import(
      /* webpackChunkName: "zip-centroids-[request]" */ `../data/zipCentroids/${prefix}.json`
    ).then(mod => mod.default || mod);
  }
  const table = await zipShardCache[prefix];
  const row = table[z];
  return row ? { lat: row[0], lng: row[1], city: row[2], state: row[3] } : null;
};
